import React, { useState } from 'react';
import styled from 'styled-components/macro';
import queryString from 'query-string';
import base64url from 'base64url';
import { authStore } from 'stores';
import { Trans } from '@lingui/macro';

const AppleButton = styled.button`
    cursor: pointer;
    display: block;
    background: #000;
    width: 100%;
    color: white;
    border-radius: 30px;
    text-align: center;
    padding: 12px 32px;
    outline: 0;
    text-decoration: none;
    font-size: 1.125rem;
`;

const Container = styled.div`
    min-height: 40px;
    margin: 20px auto;
`;

// basically it should go from '?continue=/blah?foo=1&bar=3'
// to: /blah?foo=1&bar=3
export const prepareSuccessRedirect = ({
    search,
}: {
    search?: string;
}): string => {
    if (typeof search !== 'string') return '/';
    let paramsWithoutContinue = queryString.parse(decodeURIComponent(search));
    delete paramsWithoutContinue['continue'];

    const continueTo = queryString.parse(decodeURIComponent(search)).continue;

    if (typeof continueTo === 'string') {
        const continuePath = continueTo.split('?')[0];
        const continueSearch = queryString.parse(continueTo.split('?')[1]);
        return (
            continuePath +
            '?' +
            // keep only the params part
            queryString.stringify({
                ...paramsWithoutContinue,
                ...continueSearch,
            })
        );
    } else if (Array.isArray(continueTo) && typeof continueTo[0] === 'string') {
        // this case shouldn't really happen, but it's possible, in case the same key is repeated in params
        return continueTo[0] + search;
    } else {
        return '/' + search;
    }
};

// set in apple dashboard
const yousicianAccountSiteClientId = 'com.yousician.account';

const AppleSignInButton = () => {
    const [failedToLoad, setFailedToLoad] = useState(false);

    // so that we don't load the apple signin script on every page of the website
    const loadAppleSignInScript = () => {
        if ('AppleID' in window) {
            initAppleSignIn();
            return;
        }

        const script = document.createElement('script');
        script.src =
            'https://appleid.cdn-apple.com/appleauth/static/jsapi/appleid/1/en_US/appleid.auth.js';
        script.async = true;
        script.onload = initAppleSignIn;

        document.body.appendChild(script);
    };

    const initAppleSignIn = async () => {
        try {
            // state is the CSRF token ;
            const { state } = await authStore.initAppleSignIn();

            // after apple is done with sign in, users are sent to:
            const backendAppleSignInProcess =
                (window as any).yconfig.ApiUrl + '/process-apple-signin';

            (window as any).AppleID.auth.init({
                clientId: yousicianAccountSiteClientId,
                redirectURI: backendAppleSignInProcess,
                state:
                    'token=' +
                    state +
                    '&return_to=' +
                    // to make sure nested params don't get lost in trasnfer, encode them in base64
                    base64url.encode(
                        prepareSuccessRedirect({
                            search: decodeURIComponent(window.location.search),
                        }),
                    ),
                usePopup: false,
            });

            // ready to go to apple!
            (window as any).AppleID.auth.signIn();
        } catch (e) {
            console.error(e);
            setFailedToLoad(true);
        }
    };

    // problem in loading apple login button
    if (failedToLoad) return null;

    return (
        <Container>
            <AppleButton onClick={loadAppleSignInScript}>
                <Trans>Log in with Apple</Trans>
            </AppleButton>
        </Container>
    );
};

export default AppleSignInButton;
